import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Newspaper, BrainCircuit, ArrowLeft, Ban } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { fmt } from "@/lib/api";
import PriceChart from "@/components/PriceChart";
import TradeDialog from "@/components/TradeDialog";
import { SignalBadge, QualityTag, TrendTag, ConfidenceBar, StatusTag } from "@/components/Badges";

const TF_NAMES = { "1d": "Daily", "4h": "4H", "1h": "1H", "15m": "15M" };

const Row = ({ k, v, c = "text-zinc-800", testid }) => (
  <div className="flex justify-between py-1.5 border-b border-zinc-100 text-xs"><span className="text-zinc-500">{k}</span><span className={`num ${c}`} data-testid={testid}>{v}</span></div>
);

function MLBlock({ ml }) {
  return (
    <div className="panel p-4 ai-glow" data-testid="ml-prediction">
      <div className="flex items-center gap-2 mb-3"><BrainCircuit size={14} className="text-[#8B5CF6]" /><span className="label-xs text-[#7C3AED]">AI Prediction</span></div>
      {[["Bullish", ml.bullish, "#059669"], ["Bearish", ml.bearish, "#DC2626"], ["Neutral", ml.neutral, "#71717A"]].map(([l, v, c]) => (
        <div key={l} className="mb-2">
          <div className="flex justify-between text-xs mb-1"><span className="text-zinc-500">{l}</span><span className="num text-zinc-900">{v}%</span></div>
          <div className="h-1 bg-zinc-200"><div className="h-full transition-[width] duration-700" style={{ width: `${v}%`, background: c }} /></div>
        </div>
      ))}
      <p className="text-[10px] text-zinc-500 mt-2">{ml.model}</p>
    </div>
  );
}

export default function SignalDetail() {
  const { pair } = useParams();
  const { data: a, loading } = usePoll(`/analysis/${pair}`, 60000, [pair]);
  const { data: chart } = usePoll(`/chart/${pair}?tf=1h&n=300`, 60000, [pair]);
  const [open, setOpen] = useState(false);
  const tradable = a && (a.signal === "BUY" || a.signal === "SELL");
  const levels = useMemo(() => (a ? [
    { price: a.entry, color: "#007AFF", title: "ENTRY" }, { price: a.stop_loss, color: "#DC2626", title: "SL" },
    { price: a.tp1, color: "#059669", title: "TP1" }, { price: a.tp2, color: "#059669", title: "TP2", dashed: true },
    { price: a.tp3, color: "#05966988", title: "TP3", dashed: true, width: 1 },
  ] : []), [a]);
  const overlays = useMemo(() => ["ema20", "ema50", "ema200"], []);
  if (loading && !a) return <div className="label-xs" data-testid="signal-detail-loading">Analyzing {pair}…</div>;
  if (!a) return <div className="text-zinc-500">Instrument not found.</div>;
  const dg = a.digits;
  const i1 = a.indicators["1h"];
  const pa = a.price_action["1h"];
  return (
    <div data-testid="signal-detail-page">
      <Link to="/scanner" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3" data-testid="back-to-scanner"><ArrowLeft size={12} />Scanner</Link>
      <div className="flex flex-wrap items-center gap-4 mb-5 fade-up">
        <h1 className="text-3xl sm:text-4xl font-black num text-zinc-900" data-testid="signal-detail-symbol">{a.symbol}</h1>
        <SignalBadge signal={a.signal} testid="signal-detail-badge" />
        <QualityTag quality={a.quality} />
        <span className="num text-xl text-zinc-800" data-testid="signal-detail-price">{fmt(a.quote.price, dg)}</span>
        <span className="num text-xs text-zinc-500">Bid {fmt(a.quote.bid, dg)} · Ask {fmt(a.quote.ask, dg)} · Spread {a.quote.spread_pips}p</span>
        {a.tracked_signal && <span className="text-xs text-zinc-500">Tracked: <StatusTag status={a.tracked_signal.status} testid="tracked-signal-status" /></span>}
        <div className="ml-auto flex gap-2">
          <Link to={`/ai?pair=${pair}`} className="px-3 py-2 text-xs border border-[#8B5CF6]/50 text-[#7C3AED] rounded-sm hover:bg-[#8B5CF6]/10 transition-colors" data-testid="ai-commentary-link">AI Commentary</Link>
          <button onClick={() => setOpen(true)} data-testid="accept-signal-btn"
            className={`px-4 py-2 text-xs font-bold rounded-sm transition-[background-color,transform] active:scale-95 ${a.direction === "BUY" ? "bg-[#059669] text-white hover:bg-[#047857]" : "bg-[#DC2626] hover:bg-[#B91C1C] text-white"}`}>
            {tradable ? `Accept ${a.signal} · Paper Trade` : "Open Paper Trade"}
          </button>
        </div>
      </div>
      {a.no_trade_reason && (
        <div className="panel border-l-2 border-l-[#D97706] p-3 mb-4 flex items-center gap-2 text-sm" data-testid="no-trade-reason">
          <Ban size={14} className="text-[#D97706]" /><span className="text-zinc-700"><b className="text-zinc-900">{a.signal}:</b> {a.no_trade_reason}</span>
        </div>
      )}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <div className="panel p-2" data-testid="signal-chart-panel">
            <div className="flex justify-between items-center px-2 pb-2 text-[11px] text-zinc-500">
              <span>1H · EMA20 <span className="text-[#D97706]">━</span> EMA50 <span className="text-[#007AFF]">━</span> EMA200 <span className="text-zinc-500">━</span> · S/R dashed</span>
              <span>{chart?.source}</span>
            </div>
            {chart ? <PriceChart data={chart} levels={levels} overlays={overlays} digits={dg} /> : <div className="h-[460px] grid place-items-center label-xs">Loading chart…</div>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mtf-grid">
            {Object.entries(TF_NAMES).map(([tf, name]) => {
              const m = a.mtf[tf];
              return (
                <div key={tf} className={`panel p-3 border-t-2 ${m.label === "Bullish" ? "border-t-[#059669]" : m.label === "Bearish" ? "border-t-[#DC2626]" : "border-t-zinc-300"}`} data-testid={`mtf-${tf}`}>
                  <p className="label-xs mb-1">{name}</p><TrendTag trend={m.label} />
                  <p className="num text-[11px] text-zinc-500 mt-2">RSI {m.rsi} · ADX {m.adx}</p>
                  <p className="text-[11px] text-zinc-500">Structure: {m.structure}</p>
                </div>
              );
            })}
          </div>
          <div className="panel p-5" data-testid="why-signal-section">
            <h3 className="font-display font-black text-sm tracking-wide mb-4">WHY FX COTRADER GENERATED THIS SIGNAL</h3>
            <ol className="space-y-2.5">
              {a.reasons.map((r, i) => (
                <li key={i} className="flex gap-3 text-sm text-zinc-700 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`reason-${i}`}>
                  <CheckCircle2 size={16} className="text-[#059669] shrink-0 mt-0.5" /><span><span className="num text-zinc-500 mr-1">{i + 1}.</span>{r}</span>
                </li>
              ))}
              {a.warnings.map((w, i) => (
                <li key={`w${i}`} className="flex gap-3 text-sm text-zinc-500" data-testid={`warning-${i}`}>
                  <AlertTriangle size={16} className="text-[#D97706] shrink-0 mt-0.5" />{w}
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-zinc-500 mt-4">Strategy logic: {a.strategy_logic} · Regime-aware scoring. Probabilistic assessment only — not a guarantee.</p>
          </div>
          <div className="panel p-5" data-testid="score-breakdown">
            <h3 className="font-display font-bold text-sm mb-3">Score Breakdown <span className="num text-zinc-500 font-normal">({a.base_score}/100 rule score)</span></h3>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {Object.entries(a.score_breakdown).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3 text-xs">
                  <span className="w-24 text-zinc-500 capitalize">{{ htf: "HTF trend", sr: "S/R zone", rr: "Risk/reward" }[k] || k}</span>
                  <div className="flex-1 h-1 bg-zinc-200"><div className="h-full bg-[#007AFF] text-white" style={{ width: `${(v.points / v.max) * 100}%` }} /></div>
                  <span className="num w-10 text-right text-zinc-700">{v.points}/{v.max}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <aside className="col-span-12 lg:col-span-4 space-y-4">
          <div className="panel p-4" data-testid="trade-plan">
            <div className="flex justify-between items-center mb-3"><span className="label-xs">Trade Plan · {a.entry_type}</span><span className="num text-[10px] text-zinc-500">{a.timeframe} · {a.expected_duration}</span></div>
            <ConfidenceBar value={a.confidence} testid="detail-confidence" />
            <div className="mt-3">
              <Row k="Direction" v={a.direction} c={a.direction === "BUY" ? "text-[#059669]" : "text-[#DC2626]"} />
              <Row k="Entry Zone" v={`${fmt(a.entry_zone[0], dg)} – ${fmt(a.entry_zone[1], dg)}`} testid="detail-entry-zone" />
              <Row k="Stop Loss" v={`${fmt(a.stop_loss, dg)} (${a.sl_pips}p)`} c="text-[#DC2626]" testid="detail-stop-loss" />
              <Row k="Take Profit 1" v={fmt(a.tp1, dg)} c="text-[#059669]" testid="detail-tp1" />
              <Row k="Take Profit 2" v={fmt(a.tp2, dg)} c="text-[#059669]" testid="detail-tp2" />
              <Row k="Take Profit 3" v={fmt(a.tp3, dg)} c="text-[#059669]/70" testid="detail-tp3" />
              <Row k="Risk / Reward" v={`1:${a.risk_reward}`} testid="detail-rr" />
              <Row k="Trend (4H)" v={a.trend} />
              <Row k="Regime" v={a.regime} />
              <Row k="Volatility" v={a.volatility} />
              <Row k="Created" v={new Date(a.created_at).toUTCString().slice(17, 25)} />
              <Row k="Expires" v={new Date(a.expires_at).toUTCString().slice(5, 22)} />
            </div>
          </div>
          <MLBlock ml={a.ml} />
          <div className="panel p-4" data-testid="news-risk">
            <div className="flex items-center gap-2 mb-2"><Newspaper size={14} className="text-zinc-500" /><span className="label-xs">Economic News Risk</span></div>
            {a.news ? (
              <p className="text-sm text-zinc-700"><span className="text-[#DC2626] font-semibold">{a.news.impact}</span> · {a.news.currency} {a.news.event} in <span className="num">{a.news.minutes_until}</span> min</p>
            ) : <p className="text-sm text-zinc-500">No high-impact {a.symbol.slice(0, 3)}/{a.symbol.slice(4)} events within 4 hours.</p>}
            <p className="text-[10px] text-zinc-500 mt-2">Signals are suspended {a.news_window} min before high-impact events (Settings).</p>
          </div>
          <div className="panel p-4" data-testid="indicator-readings">
            <span className="label-xs">Technical Readings · 1H</span>
            <div className="mt-2">
              <Row k="RSI 14" v={i1.rsi?.toFixed(1)} /><Row k="MACD hist" v={i1.macd_hist?.toFixed(dg + 1)} c={i1.macd_hist > 0 ? "text-[#059669]" : "text-[#DC2626]"} />
              <Row k="ADX" v={`${i1.adx?.toFixed(1)} (${i1.trend_strength})`} /><Row k="ATR" v={fmt(i1.atr, dg)} />
              <Row k="Stoch RSI K/D" v={`${i1.stoch_k?.toFixed(0)}/${i1.stoch_d?.toFixed(0)}`} />
              <Row k="EMA 9/20/50" v={`${fmt(i1.ema9, dg)} / ${fmt(i1.ema20, dg)} / ${fmt(i1.ema50, dg)}`} />
              <Row k="EMA 100/200" v={`${fmt(i1.ema100, dg)} / ${fmt(i1.ema200, dg)}`} />
              <Row k="SMA 50/200" v={`${fmt(i1.sma50, dg)} / ${fmt(i1.sma200, dg)}`} />
              <Row k="Bollinger" v={`${fmt(i1.bb_lower, dg)} – ${fmt(i1.bb_upper, dg)}`} />
              <Row k="Cross signals" v={[i1.golden_cross && "Golden", i1.death_cross && "Death", i1.ema_crossover && `EMA9/20 ${i1.ema_crossover}`].filter(Boolean).join(", ") || "None"} />
              <Row k="Volatility" v={i1.volatility_expansion ? "Expanding" : i1.volatility_contraction ? "Contracting" : "Stable"} />
            </div>
          </div>
          <div className="panel p-4" data-testid="price-action-readings">
            <span className="label-xs">Price Action · 1H</span>
            <div className="mt-2">
              <Row k="Structure" v={pa.structure} /><Row k="BOS / CHOCH" v={`${pa.bos || "—"} / ${pa.choch || "—"}`} />
              <Row k="Support" v={pa.support.map((x) => fmt(x, dg)).join(", ") || "—"} c="text-[#059669]" />
              <Row k="Resistance" v={pa.resistance.map((x) => fmt(x, dg)).join(", ") || "—"} c="text-[#DC2626]" />
              <Row k="Retest" v={pa.retest || "—"} /><Row k="Liquidity sweep" v={pa.liquidity_sweep ? "Yes" : "—"} />
              <Row k="False breakout" v={pa.false_breakout || "—"} /><Row k="Consolidation" v={pa.consolidation ? "Yes" : "No"} />
              <Row k="Patterns (1H/15M)" v={[...pa.patterns, ...a.price_action["15m"].patterns].map((p) => p.name).join(", ") || "None"} />
              <Row k="Pivot P / R1 / S1" v={`${fmt(a.pivots.P, dg)} / ${fmt(a.pivots.R1, dg)} / ${fmt(a.pivots.S1, dg)}`} />
              <Row k="Fib 0.618" v={fmt(a.fibonacci.retracements["0.618"], dg)} />
            </div>
          </div>
        </aside>
      </div>
      <TradeDialog open={open} onOpenChange={setOpen}
        preset={{ symbol: a.symbol, direction: a.direction, stop_loss: a.stop_loss, tp1: a.tp1, entry: a.entry, digits: dg, confidence: a.confidence, signal_id: a.tracked_signal?.id }} />
    </div>
  );
}
